import React, { useEffect, useRef, useState } from 'react'
import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  onSnapshot, orderBy, query, serverTimestamp, writeBatch, getCountFromServer
} from 'firebase/firestore'
import { db } from '../../firebase/firebase'
import AdminLayout from './AdminLayout'
import { pickDailyQuestions, todayStr } from '../../utils/quizUtils'

const EMPTY = {
  ua: '', en: '',
  ou0: '', ou1: '', ou2: '', ou3: '',
  oe0: '', oe1: '', oe2: '', oe3: '',
  c: '0',
  fu: '', fe: '',
}

const AdminQuiz = () => {
  const [questions, setQuestions] = useState([])
  const [form, setForm] = useState(EMPTY)
  const [editId, setEditId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [seeding, setSeeding] = useState(false)
  const [seedDone, setSeedDone] = useState(false)
  const [seedProgress, setSeedProgress] = useState('')
  const [selected, setSelected] = useState(new Set())
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [search, setSearch] = useState('')
  const [totalCount, setTotalCount] = useState(null)
  const [dupWarning, setDupWarning] = useState('')
  const [previewDate, setPreviewDate] = useState(todayStr)
  const selectAllRef = useRef(null)

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'quizQuestions'), orderBy('createdAt', 'desc')),
      snap => {
        setQuestions(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        setSelected(new Set())
      }
    )
    getCountFromServer(collection(db, 'quizQuestions'))
      .then(s => setTotalCount(s.data().count))
      .catch(() => {})
    return unsub
  }, [])

  useEffect(() => { setDupWarning('') }, [form.ua])

  useEffect(() => {
    if (!selectAllRef.current) return
    const visible = filtered()
    const allChecked = visible.length > 0 && visible.every(q => selected.has(q.id))
    const someChecked = visible.some(q => selected.has(q.id)) && !allChecked
    selectAllRef.current.checked = allChecked
    selectAllRef.current.indeterminate = someChecked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, questions, search])

  const filtered = () => {
    if (!search.trim()) return questions
    const s = search.toLowerCase()
    return questions.filter(q =>
      q.ua?.toLowerCase().includes(s) || q.en?.toLowerCase().includes(s)
    )
  }

  const toggleOne = (id) =>
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  const toggleAll = () => {
    const vis = filtered()
    const allSel = vis.every(q => selected.has(q.id))
    setSelected(prev => {
      const n = new Set(prev)
      vis.forEach(q => allSel ? n.delete(q.id) : n.add(q.id))
      return n
    })
  }

  const f = (key, ph, required = false, textarea = false) => (
    <div style={s.field}>
      <label style={s.label}>{ph}</label>
      {textarea
        ? <textarea value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
            style={{ ...s.input, height: '68px', resize: 'vertical' }} placeholder={ph} required={required} />
        : <input value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
            style={s.input} placeholder={ph} required={required} />}
    </div>
  )

  const toDoc = () => ({
    ua: form.ua.trim(), en: form.en.trim(),
    ou: [form.ou0, form.ou1, form.ou2, form.ou3].map(s => s.trim()),
    oe: [form.oe0, form.oe1, form.oe2, form.oe3].map(s => s.trim()),
    c: Number(form.c),
    fu: form.fu.trim(), fe: form.fe.trim(),
  })

  const fromDoc = (q) => ({
    ua: q.ua || '', en: q.en || '',
    ou0: q.ou?.[0] || '', ou1: q.ou?.[1] || '', ou2: q.ou?.[2] || '', ou3: q.ou?.[3] || '',
    oe0: q.oe?.[0] || '', oe1: q.oe?.[1] || '', oe2: q.oe?.[2] || '', oe3: q.oe?.[3] || '',
    c: String(q.c ?? 0),
    fu: q.fu || '', fe: q.fe || '',
  })

  const handleSave = async (e) => {
    e.preventDefault()
    setDupWarning('')
    if (!editId) {
      const uaNorm = form.ua.trim().toLowerCase()
      const isDup = questions.some(q => q.ua?.trim().toLowerCase() === uaNorm)
      if (isDup) { setDupWarning('Питання з таким текстом вже є в базі.'); return }
    }
    setSaving(true)
    try {
      if (editId) {
        await updateDoc(doc(db, 'quizQuestions', editId), toDoc())
        setEditId(null)
      } else {
        await addDoc(collection(db, 'quizQuestions'), { ...toDoc(), createdAt: serverTimestamp() })
      }
      setForm(EMPTY)
    } catch (err) { alert('Помилка: ' + err.message) }
    finally { setSaving(false) }
  }

  const handleEdit = (q) => { setEditId(q.id); setForm(fromDoc(q)); window.scrollTo(0, 0) }
  const cancelEdit = () => { setEditId(null); setForm(EMPTY) }

  const handleDelete = async (id) => {
    if (!window.confirm('Видалити це питання?')) return
    await deleteDoc(doc(db, 'quizQuestions', id))
  }

  const handleBulkDelete = async () => {
    const count = selected.size
    if (!window.confirm(`Видалити ${count} питань?`)) return
    setBulkDeleting(true)
    try {
      const batch = writeBatch(db)
      selected.forEach(id => batch.delete(doc(db, 'quizQuestions', id)))
      await batch.commit()
    } catch (err) { alert('Помилка: ' + err.message) }
    finally { setBulkDeleting(false) }
  }

  const handleSeed = async () => {
    let SEED
    try {
      const mod = await import('../../assets/data/quizSeed.js')
      SEED = mod.QUIZ_SEED
    } catch { alert('Файл quizSeed.js не знайдено'); return }
    const existingSet = new Set(questions.map(q => q.ua?.trim().toLowerCase()))
    const fresh = SEED.filter(q => !existingSet.has(q.ua?.trim().toLowerCase()))
    if (fresh.length === 0) { alert('Усі питання вже є в базі. Нічого не додано.'); return }
    const skipCount = SEED.length - fresh.length
    const msg = skipCount > 0
      ? `Завантажити ${fresh.length} питань? (${skipCount} дублікат${skipCount === 1 ? '' : 'ів'} буде пропущено)`
      : `Завантажити ${fresh.length} питань у базу?`
    if (!window.confirm(msg)) return
    setSeeding(true)
    try {
      const BATCH = 400
      let count = 0
      for (let i = 0; i < fresh.length; i += BATCH) {
        const batch = writeBatch(db)
        fresh.slice(i, i + BATCH).forEach(q => {
          batch.set(doc(collection(db, 'quizQuestions')), { ...q, createdAt: serverTimestamp() })
        })
        await batch.commit()
        count += Math.min(BATCH, fresh.length - i)
        setSeedProgress(`${count}/${fresh.length}`)
      }
      setSeedDone(true)
    } catch (err) { alert('Помилка: ' + err.message) }
    finally { setSeeding(false); setSeedProgress('') }
  }

  const vis = filtered()
  const LETTERS = ['А', 'Б', 'В', 'Г']

  return (
    <AdminLayout>
      <div style={s.page}>
        <div style={s.pageHeader}>
          <div>
            <h1 style={s.pageTitle}>Біблійний Квіз — Питання</h1>
          </div>
          <div style={s.headerActions}>
            {selected.size > 0 && (
              <button onClick={handleBulkDelete} disabled={bulkDeleting} style={s.btnDanger}>
                {bulkDeleting ? 'Видалення…' : `🗑 Видалити (${selected.size})`}
              </button>
            )}
            <button onClick={handleSeed} disabled={seeding} style={s.btnSeed}>
              {seeding ? `Завантаження… ${seedProgress}` : '📦 Завантажити seed-питання'}
            </button>
            {seedDone && <span style={s.ok}>✓ Завантажено</span>}
          </div>
        </div>

        <div style={s.cols}>
          {/* ── Form ── */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <span style={s.cardIcon}>{editId ? '✏️' : '➕'}</span>
              <span style={s.cardTitle}>{editId ? 'Редагувати питання' : 'Нове питання'}</span>
              {editId && <button onClick={cancelEdit} style={s.btnCancel}>✕</button>}
            </div>
            <form onSubmit={handleSave} style={s.form}>

              <div style={s.section}>
                <div style={s.sectionLabel}>🇺🇦 Питання (UA)</div>
                {f('ua', 'Текст питання українською', true, true)}
                {['ou0','ou1','ou2','ou3'].map((k, i) => (
                  <div key={k} style={s.optRow}>
                    <span style={{
                      ...s.optLetter,
                      ...(String(i) === form.c ? s.optLetterCorrect : {})
                    }}>{LETTERS[i]}</span>
                    <input value={form[k]} onChange={e => setForm(p => ({ ...p, [k]: e.target.value }))}
                      style={s.optInput} placeholder={`Варіант ${LETTERS[i]} (UA)`} required />
                  </div>
                ))}
              </div>

              <div style={s.section}>
                <div style={s.sectionLabel}>🇬🇧 Question (EN)</div>
                {f('en', 'Question text in English', false, true)}
                {['oe0','oe1','oe2','oe3'].map((k, i) => (
                  <div key={k} style={s.optRow}>
                    <span style={{
                      ...s.optLetter,
                      ...(String(i) === form.c ? s.optLetterCorrect : {})
                    }}>{LETTERS[i]}</span>
                    <input value={form[k]} onChange={e => setForm(p => ({ ...p, [k]: e.target.value }))}
                      style={s.optInput} placeholder={`Option ${LETTERS[i]} (EN)`} />
                  </div>
                ))}
              </div>

              <div style={s.section}>
                <div style={s.sectionLabel}>✅ Правильна відповідь</div>
                <div style={s.radioRow}>
                  {LETTERS.map((l, i) => (
                    <label key={i} style={{
                      ...s.radioLabel,
                      ...(form.c === String(i) ? s.radioLabelActive : {})
                    }}>
                      <input type='radio' name='correct' value={String(i)}
                        checked={form.c === String(i)}
                        onChange={e => setForm(p => ({ ...p, c: e.target.value }))}
                        style={{ display: 'none' }} />
                      {l}
                    </label>
                  ))}
                </div>
              </div>

              <div style={s.section}>
                <div style={s.sectionLabel}>💡 Цікавий факт</div>
                {f('fu', 'Факт / пояснення (UA)', false, true)}
                {f('fe', 'Fact / explanation (EN)', false, true)}
              </div>

              {dupWarning && <div style={s.dupWarn}>⚠️ {dupWarning}</div>}
              <button type='submit' disabled={saving} style={s.btnPrimary}>
                {saving ? 'Збереження…' : editId ? '💾 Зберегти зміни' : '➕ Додати питання'}
              </button>
            </form>
          </div>

          {/* ── List ── */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <span style={s.cardIcon}>📋</span>
              <span style={s.cardTitle}>Усі питання</span>
              <span style={s.badge}>{questions.length}</span>
            </div>

            <input value={search} onChange={e => setSearch(e.target.value)}
              style={{ ...s.input, marginBottom: '12px' }}
              placeholder='🔍 Пошук по тексту…' />

            {vis.length === 0
              ? <div style={s.empty}>Питань немає. Додайте вручну або завантажте seed.</div>
              : <>
                <div style={s.selectAllRow}>
                  <label style={s.checkLabel}>
                    <input ref={selectAllRef} type='checkbox' onChange={toggleAll} style={s.checkbox} />
                    <span style={s.selectAllText}>
                      {selected.size === 0 ? 'Виділити всі' : `Виділено: ${selected.size}`}
                    </span>
                  </label>
                </div>
                <div style={s.list}>
                  {vis.map((q, idx) => (
                    <div key={q.id} style={{ ...s.row, ...(selected.has(q.id) ? s.rowSel : {}) }}
                      onClick={() => toggleOne(q.id)}>
                      <input type='checkbox' checked={selected.has(q.id)}
                        onChange={() => toggleOne(q.id)}
                        onClick={e => e.stopPropagation()} style={s.checkbox} />
                      <div style={s.rowContent}>
                        <div style={s.rowQ}>{q.ua}</div>
                        {q.ou && <div style={s.rowOpts}>
                          {q.ou.map((o, i) => (
                            <span key={i} style={{ ...s.rowOpt, ...(i === q.c ? s.rowOptCorrect : {}) }}>
                              {LETTERS[i]}: {o}
                            </span>
                          ))}
                        </div>}
                      </div>
                      <div style={s.rowActions} onClick={e => e.stopPropagation()}>
                        <button onClick={() => handleEdit(q)} style={s.btnEdit}>✏️</button>
                        <button onClick={() => handleDelete(q.id)} style={s.btnDel}>🗑</button>
                      </div>
                    </div>
                  ))}
                </div>
              </>}
          </div>
        </div>
      </div>

      {/* Questions by date */}
      {(() => {
        const previewQs = pickDailyQuestions(questions, previewDate)
        const isToday = previewDate === todayStr()
        return (
          <div style={{ ...s.card, marginTop: '24px' }}>
            <div style={s.cardHeader}>
              <span style={s.cardIcon}>📅</span>
              <span style={s.cardTitle}>Питання на дату</span>
              <input
                type='date'
                value={previewDate}
                onChange={e => setPreviewDate(e.target.value)}
                style={s.dateInput}
              />
              {isToday && <span style={s.badge}>сьогодні</span>}
              <span style={{ ...s.badge, marginLeft: '4px' }}>{previewQs.length} / 10</span>
            </div>
            {previewQs.length === 0
              ? <div style={s.empty}>Питань ще немає — додайте хоча б 10.</div>
              : <div style={s.todayGrid}>
                  {previewQs.map((q, idx) => (
                    <div key={q.id} style={s.todayRow}>
                      <div style={s.todayNum}>{idx + 1}</div>
                      <div style={s.rowContent}>
                        <div style={s.rowQ}>{q.ua}</div>
                        {q.ou && <div style={s.rowOpts}>
                          {q.ou.map((o, i) => (
                            <span key={i} style={{ ...s.rowOpt, ...(i === q.c ? s.rowOptCorrect : {}) }}>
                              {LETTERS[i]}: {o}
                            </span>
                          ))}
                        </div>}
                      </div>
                      <div style={s.rowActions}>
                        <button onClick={() => handleEdit(q)} style={s.btnEdit}>✏️</button>
                      </div>
                    </div>
                  ))}
                </div>
            }
          </div>
        )
      })()}
    </AdminLayout>
  )
}

const s = {
  page: { maxWidth: '1200px' },
  pageHeader: { display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '28px', flexWrap: 'wrap' },
  pageTitle: { margin: '0 0 4px', fontSize: '24px', fontWeight: '700', color: '#2d1f14' },
  pageDesc: { margin: 0, fontSize: '13px', color: '#9a7c63' },
  headerActions: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginLeft: 'auto' },
  ok: { fontSize: '13px', color: '#3a8a3a', fontWeight: '600' },
  cols: { display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '24px', alignItems: 'start' },
  card: { background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 16px rgba(92,61,46,0.07)', border: '1px solid #f0e4d4' },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', paddingBottom: '14px', borderBottom: '1px solid #f5ede3' },
  cardIcon: { fontSize: '16px' },
  cardTitle: { fontSize: '15px', fontWeight: '700', color: '#2d1f14', flex: 1 },
  badge: { background: '#f0e6d8', color: '#8b6044', padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  form: { width: '100%', margin: 0, border: 'none', background: 'none', padding: 0, display: 'block' },
  section: { background: '#fdfaf7', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f0e4d4', marginBottom: '12px' },
  sectionLabel: { fontSize: '12px', fontWeight: '700', color: '#8b6044', marginBottom: '10px' },
  field: { marginBottom: '8px' },
  label: { display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#9a7c63', marginBottom: '4px' },
  input: { width: '100%', padding: '8px 12px', border: '1.5px solid #e8d8c8', borderRadius: '7px', fontSize: '13px', color: '#2d1f14', background: '#fff', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' },
  optRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' },
  optLetter: { width: '24px', height: '24px', borderRadius: '50%', background: '#f0e4d4', color: '#8b6044', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', flexShrink: 0 },
  optLetterCorrect: { background: '#3aaa6e', color: '#fff' },
  optInput: { flex: 1, padding: '7px 10px', border: '1.5px solid #e8d8c8', borderRadius: '6px', fontSize: '13px', color: '#2d1f14', background: '#fff', fontFamily: 'inherit', outline: 'none' },
  radioRow: { display: 'flex', gap: '8px' },
  radioLabel: { padding: '6px 16px', border: '1.5px solid #e8d8c8', borderRadius: '8px', fontSize: '13px', fontWeight: '600', color: '#9a7c63', cursor: 'pointer', userSelect: 'none' },
  radioLabelActive: { background: '#3aaa6e', borderColor: '#3aaa6e', color: '#fff' },
  btnPrimary: { padding: '10px 22px', background: 'linear-gradient(135deg,#6b4a32,#8b6044)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', width: '100%', marginTop: '4px' },
  btnSeed: { padding: '9px 18px', background: 'transparent', color: '#8b6044', border: '1.5px solid #c4986a', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 },
  btnDanger: { padding: '9px 18px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 },
  btnCancel: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#9a7c63', padding: '2px 6px' },
  selectAllRow: { padding: '6px 14px 10px', borderBottom: '1px solid #f5ede3', marginBottom: '10px' },
  checkLabel: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' },
  checkbox: { width: '15px', height: '15px', cursor: 'pointer', accentColor: '#8b6044', flexShrink: 0 },
  selectAllText: { fontSize: '12px', color: '#8b6044', fontWeight: '600' },
  empty: { color: '#b8a090', fontSize: '14px', padding: '12px 0', textAlign: 'center' },
  list: { display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '700px', overflowY: 'auto' },
  row: { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', borderRadius: '10px', background: '#fdfaf7', border: '1.5px solid #f0e4d4', cursor: 'pointer' },
  rowSel: { background: '#fdf4ec', border: '1.5px solid #c4986a' },
  rowContent: { flex: 1, minWidth: 0 },
  rowQ: { fontSize: '13px', fontWeight: '600', color: '#2d1f14', marginBottom: '6px', lineHeight: '1.4' },
  rowOpts: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  rowOpt: { fontSize: '11px', color: '#9a7c63', background: '#f0e4d4', padding: '2px 8px', borderRadius: '10px' },
  rowOptCorrect: { background: '#c8f0dc', color: '#1e7a4a', fontWeight: '700' },
  rowActions: { display: 'flex', gap: '4px', flexShrink: 0 },
  btnEdit: { background: '#f0e4d4', border: 'none', borderRadius: '6px', padding: '5px 8px', cursor: 'pointer', fontSize: '13px' },
  btnDel: { background: '#fde8e8', border: 'none', borderRadius: '6px', padding: '5px 8px', cursor: 'pointer', fontSize: '13px' },
  dupWarn: { background: '#fff8e6', border: '1px solid #f0c040', color: '#7a5c00', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '8px' },
  todayGrid: { display: 'flex', flexDirection: 'column', gap: '8px' },
  todayRow: { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', borderRadius: '10px', background: '#fdfaf7', border: '1.5px solid #f0e4d4' },
  todayNum: { width: '24px', height: '24px', borderRadius: '50%', background: '#e8d8c8', color: '#6b4a32', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', flexShrink: 0, marginTop: '1px' },
  dateInput: { padding: '5px 10px', border: '1.5px solid #e8d8c8', borderRadius: '7px', fontSize: '13px', color: '#2d1f14', background: '#fff', fontFamily: 'inherit', outline: 'none', marginLeft: 'auto' },
}

export default AdminQuiz
